import { z } from "zod";

// The toplist game as one snapshot, parked on the server between visits:
//   tags:     pick as many categories as you like, or everything
//   discover: one out of four, round after round, or find it by search (or name
//             it oneself), until there is one pick for every place
//   result:   the picks as a list to put in order, with what was passed over
//             and the search at hand to swap one; saved once every place is filled
// Plain JSON on purpose: it has to come back unchanged after a reload or on
// another device. Every transition is a pure function of the snapshot.

// Bump on any change to the snapshot shape. The version is stored next to the
// snapshot, not inside it: a snapshot of another version is not parsed at all
// but discarded, and the game starts over.
export const TOPLIST_SNAPSHOT_VERSION = 5;

// Places in the finished ranking: a top ten has ten, never fewer.
export const TOPLIST_KEEP = 10;
// Suggestions per round in the discover phase: few enough to compare at a
// glance, and a 2x2 grid still fits a phone screen.
export const TOPLIST_ROUND_SIZE = 4;
// The longest own title genesis takes.
export const OWN_TITLE_MAX_LENGTH = 120;
// Own entries live in the same id space as catalogue items (UUIDs) under a
// prefix of their own, so every list and rule here works on plain ids.
const OWN_ID_PREFIX = "own:";

export type ToplistPhase = "tags" | "discover" | "result";

export interface ToplistSnapshot {
  readonly phase: ToplistPhase;
  // Chosen category codes; empty means everything.
  readonly tags: readonly string[];
  // The suggestions on screen, up to a round's worth; none outside the rounds.
  readonly round: readonly string[];
  // The suggestions still to come, in order. Whatever a round shows and is not
  // picked goes to the back, so the suggestions never run out while a place is open.
  readonly queue: readonly string[];
  // What a round showed and was not picked, in the order it was passed over,
  // for changing one's mind in the result. A pick taken back comes first, so it
  // stays in reach. Each of them is also still in the round or the queue, or
  // one of the person's own.
  readonly passed: readonly string[];
  // The list: what the person picked, in the order they picked it until they
  // reorder it in the result. Never more than places.
  readonly selected: readonly string[];
  // Titles of the person's own, for what the catalogue lacks. Each is on the
  // list or among what was passed over, and never comes round in a round.
  readonly own: readonly ToplistOwnEntry[];
}

export interface ToplistOwnEntry {
  readonly id: string;
  readonly title: string;
}

// What a finished list is written as: a catalogue item or an own title.
export type ToplistEntry = { catalogItemId: string } | { title: string };

// The part of a catalogue item the game logic needs.
export interface ToplistCandidate {
  id: string;
  tags: readonly string[];
}

export function createToplistSnapshot(): ToplistSnapshot {
  return { phase: "tags", tags: [], round: [], queue: [], passed: [], selected: [], own: [] };
}

// An own title as genesis takes it: trimmed, 1 to 120 characters, no control
// characters. Null for anything else.
export function cleanOwnTitle(text: string): string | null {
  const title = text.trim();
  if (title.length === 0 || title.length > OWN_TITLE_MAX_LENGTH || /\p{Cc}/u.test(title)) return null;
  return title;
}

// One id per own title. genesis refuses the same title twice regardless of
// case and surrounding spaces, so two such spellings are one entry here too.
export function ownEntryId(title: string): string {
  return OWN_ID_PREFIX + title.trim().toLowerCase();
}

export function isOwnEntryId(id: string): boolean {
  return id.startsWith(OWN_ID_PREFIX);
}

// The categories that occur in the catalogue, most frequent first.
export function availableTags(catalogue: readonly ToplistCandidate[]): string[] {
  const counts = new Map<string, number>();
  for (const item of catalogue) for (const tag of item.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([tag]) => tag);
}

// The suggestions: what has a chosen category first, then the rest of the
// catalogue, so the suggestions never run out before ten are picked. Within
// each part catalogue order: genesis sends the most familiar first, and that
// order is kept, not re-sorted.
export function suggestionPool(catalogue: readonly ToplistCandidate[], tags: readonly string[]): string[] {
  const wanted = new Set(tags);
  const chosen = (item: ToplistCandidate) => item.tags.some((tag) => wanted.has(tag));
  return [...catalogue.filter(chosen), ...catalogue.filter((item) => !chosen(item))].map((item) => item.id);
}

export function toggleTag(snapshot: ToplistSnapshot, tag: string): ToplistSnapshot {
  if (snapshot.tags.includes(tag)) return { ...snapshot, tags: snapshot.tags.filter((t) => t !== tag) };
  return { ...snapshot, tags: [...snapshot.tags, tag] };
}

export function clearTags(snapshot: ToplistSnapshot): ToplistSnapshot {
  return { ...snapshot, tags: [] };
}

// From the categories to the first round: the whole pool queues up, minus
// what is already picked.
export function startDiscover(snapshot: ToplistSnapshot, pool: readonly string[]): ToplistSnapshot {
  const picked = new Set(snapshot.selected);
  const queue = pool.filter((id) => !picked.has(id));
  return fillRound({ ...snapshot, phase: "discover", round: [], queue, passed: [] });
}

// Back to the categories; the picks stay, the suggestions start over, and with
// them what was passed over, own titles included.
export function backToTags(snapshot: ToplistSnapshot): ToplistSnapshot {
  const own = snapshot.own.filter((entry) => snapshot.selected.includes(entry.id));
  return { ...snapshot, phase: "tags", round: [], queue: [], passed: [], own };
}

// Takes one entry onto the list: in the rounds from the round or from anywhere
// else (the search), in the result from what was passed over or the search.
// In the rounds it ends the round, the others go to the back of the queue, and
// the pick that fills the last place goes straight on to the result.
export function choose(snapshot: ToplistSnapshot, id: string, keep: number): ToplistSnapshot {
  const { phase, round, queue, passed, selected } = snapshot;
  if ((phase !== "discover" && phase !== "result") || selected.includes(id) || selected.length >= keep) {
    return snapshot;
  }
  const others = round.filter((other) => other !== id);
  const next: ToplistSnapshot = {
    ...snapshot,
    round: [],
    queue: [...queue.filter((other) => other !== id), ...others],
    passed: passOver(passed.filter((other) => other !== id), others),
    selected: [...selected, id],
  };
  if (phase === "result") return next;
  return next.selected.length === keep ? { ...next, phase: "result" } : fillRound(next);
}

// Takes a title of the person's own like any pick, for what the catalogue
// lacks. The same title again, in any case, is the same entry.
export function takeOwn(snapshot: ToplistSnapshot, text: string, keep: number): ToplistSnapshot {
  const title = cleanOwnTitle(text);
  if (title === null) return snapshot;
  const id = ownEntryId(title);
  const withOwn = snapshot.own.some((entry) => entry.id === id)
    ? snapshot
    : { ...snapshot, own: [...snapshot.own, { id, title }] };
  const next = choose(withOwn, id, keep);
  return next === withOwn ? snapshot : next;
}

// "None of these": the round goes to the back, the next one comes up. Nothing
// to do when the round already shows everything that is left.
export function skipRound(snapshot: ToplistSnapshot): ToplistSnapshot {
  if (!canSkipRound(snapshot)) return snapshot;
  return fillRound({
    ...snapshot,
    round: [],
    queue: [...snapshot.queue, ...snapshot.round],
    passed: passOver(snapshot.passed, snapshot.round),
  });
}

export function canSkipRound(snapshot: ToplistSnapshot): boolean {
  return snapshot.phase === "discover" && snapshot.queue.length > 0;
}

// Takes an entry off the list. It leads what was passed over, so a slip of
// the finger costs nothing, and queues up again; an own title does not, the
// rounds show the catalogue only.
export function unchoose(snapshot: ToplistSnapshot, id: string): ToplistSnapshot {
  const { phase, selected } = snapshot;
  if ((phase !== "discover" && phase !== "result") || !selected.includes(id)) return snapshot;
  const next: ToplistSnapshot = {
    ...snapshot,
    queue: isOwnEntryId(id) ? snapshot.queue : [...snapshot.queue, id],
    passed: [id, ...snapshot.passed],
    selected: selected.filter((other) => other !== id),
  };
  return phase === "discover" ? fillRound(next) : next;
}

// From the result back to the rounds, for fresh suggestions while a place is open.
export function backToRounds(snapshot: ToplistSnapshot, keep: number): ToplistSnapshot {
  if (snapshot.phase !== "result" || snapshot.selected.length >= keep) return snapshot;
  return fillRound({ ...snapshot, phase: "discover" });
}

// Moves one entry of the list.
export function moveInResult(snapshot: ToplistSnapshot, from: number, to: number): ToplistSnapshot {
  const { selected } = snapshot;
  if (snapshot.phase !== "result" || from === to || !(from in selected) || !(to in selected)) return snapshot;
  const next = [...selected];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as string);
  return { ...snapshot, selected: next };
}

// The ranking to save, best first; null until every place is filled.
export function toplistEntries(snapshot: ToplistSnapshot, keep: number): ToplistEntry[] | null {
  if (snapshot.phase !== "result" || snapshot.selected.length !== keep) return null;
  const titles = new Map(snapshot.own.map((entry) => [entry.id, entry.title]));
  return snapshot.selected.map((id) => {
    const title = titles.get(id);
    return title === undefined ? { catalogItemId: id } : { title };
  });
}

// Fills the round up from the front of the queue.
function fillRound(snapshot: ToplistSnapshot): ToplistSnapshot {
  const missing = TOPLIST_ROUND_SIZE - snapshot.round.length;
  if (missing <= 0) return snapshot;
  return {
    ...snapshot,
    round: [...snapshot.round, ...snapshot.queue.slice(0, missing)],
    queue: snapshot.queue.slice(missing),
  };
}

// Adds what a round showed to what was passed over; an entry passed over
// before keeps its place.
function passOver(passed: readonly string[], shown: readonly string[]): readonly string[] {
  const known = new Set(passed);
  return [...passed, ...shown.filter((id) => !known.has(id))];
}

const id = z.string().min(1);
const ids = z.array(id);

const snapshotSchema = z.strictObject({
  phase: z.enum(["tags", "discover", "result"]),
  tags: ids,
  round: ids.max(TOPLIST_ROUND_SIZE),
  queue: ids,
  passed: ids,
  selected: ids,
  own: z.array(z.strictObject({ id, title: z.string() })),
});

const unique = (values: readonly string[]) => new Set(values).size === values.length;
const subset = (values: readonly string[], of: readonly string[]) => values.every((value) => of.includes(value));

// Validates a snapshot that came back from the server. Never throws: anything
// that is not exactly a state this module could have produced for `keep`
// places is null, and the caller starts a new game.
export function parseToplistSnapshot(value: unknown, keep: number): ToplistSnapshot | null {
  const parsed = snapshotSchema.safeParse(value);
  if (!parsed.success) return null;
  const snapshot = parsed.data;
  const { phase, tags, round, queue, passed, selected, own } = snapshot;
  const ownIds = own.map((entry) => entry.id);
  // Round, queue and list share out the pool: no entry twice, in one or across them.
  if (![tags, [...round, ...queue, ...selected], passed, ownIds].every(unique)) return null;
  if (!own.every((entry) => cleanOwnTitle(entry.title) === entry.title && entry.id === ownEntryId(entry.title))) {
    return null;
  }
  // An own title is on the list or passed over, and never in a round or the queue.
  if ([...round, ...queue].some(isOwnEntryId) || !subset(ownIds, [...selected, ...passed])) return null;
  if (!subset([...selected, ...passed].filter(isOwnEntryId), ownIds)) return null;
  if (!subset(passed, [...round, ...queue, ...ownIds])) return null;
  if (selected.length > keep) return null;
  // Before the result there is always a place open: the last pick moves on.
  if (phase !== "result" && selected.length >= keep) return null;
  if (phase === "tags" && (round.length > 0 || queue.length > 0 || passed.length > 0)) return null;
  if (phase === "discover") {
    // A full round while the queue has more, and never none while a place is open.
    if (round.length === 0 || (round.length < TOPLIST_ROUND_SIZE && queue.length > 0)) return null;
  }
  if (phase === "result" && round.length > 0) return null;
  return snapshot;
}
